import { RawHeaderNavigationEntry } from "@/components/layout/header";
import DesktopNavigationEntryPanel from "@/components/layout/header/desktop-navigation-entry-panel";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { Fragment, useRef } from "react";
import { useHeader } from "./desktop-header";

interface PageProps {
  entry: RawHeaderNavigationEntry;
  onButtonHover: (button: HTMLButtonElement, isOpen: boolean, mode: "enter" | "leave") => void;
  className?: string;
}

export default function DesktopNavigationEntry({ entry, onButtonHover, className }: PageProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isBig } = useHeader();

  const handleEnter = (isOpen: boolean) => {
    onButtonHover(buttonRef.current!, isOpen, "enter");
  };

  const handleLeave = (isOpen: boolean) => {
    onButtonHover(buttonRef.current!, isOpen, "leave");
  };

  const hasSubElements = entry.subElements && entry.subElements.length > 0;

  return (
    <>
      {!hasSubElements && entry.url && (
        <TranslatedLink
          key={entry.label}
          href={entry.url}
          className={cn(
            "group z-10 flex h-full items-center px-[15px] text-sm font-medium",
            "text-black transition-colors duration-200 ease-out hover:bg-lightgray-300",
            isBig ? "pb-[65px] pt-[72px]" : "",
            className,
          )}
        >
          <span className="transition-transform duration-200 group-hover:-skew-x-12">{entry.label}</span>
        </TranslatedLink>
      )}

      {hasSubElements && (
        <Popover key={entry.label} as={Fragment}>
          {({ open }) => (
            <>
              <PopoverButton
                ref={buttonRef}
                onMouseEnter={() => handleEnter(open)}
                onMouseLeave={() => handleLeave(open)}
                className={cn(
                  "z-10 flex h-full items-center px-[15px] text-sm font-medium outline-none",
                  "transition-colors duration-200 ease-out",
                  isBig ? "pb-[65px] pt-[72px]" : "",
                  open && "bg-lightgray-300",
                  !open && "text-black",
                  className,
                )}
              >
                <TranslatedLink href={entry.url} className={cn("transition-transform", open && "-skew-x-12")}>
                  {entry.label}
                </TranslatedLink>
              </PopoverButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <PopoverPanel
                  className="absolute inset-x-0 top-full text-sm text-black"
                  onMouseEnter={() => handleEnter(open)}
                  onMouseLeave={() => handleLeave(open)}
                >
                  {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                  <div className="absolute inset-0 top-1/2 bg-white shadow" aria-hidden="true" />

                  <div className="relative bg-lightgray-200">
                    <DesktopNavigationEntryPanel subEntries={entry.subElements!} />
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      )}
    </>
  );
}
